import React, { useState, useRef, useEffect } from 'react';
import './MovieCard.css';

const MovieCard = ({ 
  movie, 
  currentUser, 
  onUpdateRating, 
  onMoveToWatched,
  onMoveToWantToWatch,
  onDeleteMovie,
  showRatings,
  isGuest = false
}) => {
  const [isEditingRating, setIsEditingRating] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [hideButton, setHideButton] = useState(false);
  const [tempRating, setTempRating] = useState(
    movie.ratings[currentUser.username] || ''
  );
  const menuRef = useRef(null);

  const handleRatingSubmit = () => {
    const rating = parseFloat(tempRating);
    if (rating >= 0 && rating <= 10) {
      onUpdateRating(movie.id, currentUser.username, rating);
      setIsEditingRating(false);
    }
  };

  const handleRatingCancel = () => {
    setTempRating(movie.ratings[currentUser.username] || '');
    setIsEditingRating(false);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${movie.title}"?`)) {
      onDeleteMovie(movie.id);
    }
    setShowMenu(false);
  };

  const handleMoveToWantToWatch = () => {
    if (onMoveToWantToWatch) {
      onMoveToWantToWatch(movie.id);
    }
    setShowMenu(false);
  };

  const handleMarkWatched = () => {
    // Immediately hide the button
    setHideButton(true);
    // Show the spectacular confetti
    setShowConfetti(true);
    // Hide confetti after animation completes
    setTimeout(() => setShowConfetti(false), 1200);
    // Move to watched list after confetti finishes
    setTimeout(() => onMoveToWatched(movie.id), 1000);
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getUserRating = (username) => {
    return movie.ratings[username];
  };

  const getAverageRating = () => {
    const ratings = Object.values(movie.ratings);
    if (ratings.length === 0) return null;
    const sum = ratings.reduce((a, b) => a + b, 0);
    return (sum / ratings.length).toFixed(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="movie-card">
      <div className="movie-poster">
        <img 
          src={movie.poster} 
          alt={`${movie.title} poster`}
          onError={(e) => {
            e.target.src = `https://via.placeholder.com/300x450/1a1a2e/ffffff?text=${encodeURIComponent(movie.title)}`;
          }}
        />
        {!showRatings && !hideButton && (
          <div className="movie-overlay">
            <button 
              className="mark-watched-btn"
              onClick={handleMarkWatched}
            >
              Mark as Watched
            </button>
          </div>
        )}
        
        {showConfetti && (
          <div className="confetti-explosion">
            {/* First wave - large confetti */}
            <div className="confetti-piece confetti-large confetti-1">🎉</div>
            <div className="confetti-piece confetti-large confetti-2">🎊</div>
            <div className="confetti-piece confetti-large confetti-3">✨</div>
            <div className="confetti-piece confetti-large confetti-4">🎉</div>
            <div className="confetti-piece confetti-large confetti-5">🎊</div>
            <div className="confetti-piece confetti-large confetti-6">✨</div>
            <div className="confetti-piece confetti-large confetti-7">🎉</div>
            <div className="confetti-piece confetti-large confetti-8">🎊</div>
            
            {/* Second wave - medium confetti */}
            <div className="confetti-piece confetti-medium confetti-9">🎉</div>
            <div className="confetti-piece confetti-medium confetti-10">🎊</div>
            <div className="confetti-piece confetti-medium confetti-11">✨</div>
            <div className="confetti-piece confetti-medium confetti-12">🎉</div>
            <div className="confetti-piece confetti-medium confetti-13">🎊</div>
            <div className="confetti-piece confetti-medium confetti-14">✨</div>
            <div className="confetti-piece confetti-medium confetti-15">🎉</div>
            <div className="confetti-piece confetti-medium confetti-16">🎊</div>
            <div className="confetti-piece confetti-medium confetti-17">✨</div>
            <div className="confetti-piece confetti-medium confetti-18">🎉</div>
            
            {/* Third wave - small confetti */}
            <div className="confetti-piece confetti-small confetti-19">🎊</div>
            <div className="confetti-piece confetti-small confetti-20">✨</div>
            <div className="confetti-piece confetti-small confetti-21">🎉</div>
            <div className="confetti-piece confetti-small confetti-22">🎊</div>
            <div className="confetti-piece confetti-small confetti-23">✨</div>
            <div className="confetti-piece confetti-small confetti-24">🎉</div>
            <div className="confetti-piece confetti-small confetti-25">🎊</div>
            <div className="confetti-piece confetti-small confetti-26">✨</div>
            
            {/* Burst effect background */}
            <div className="confetti-burst"></div>
          </div>
        )}
        
        <div className="movie-menu" ref={menuRef}>
          <button 
            className="menu-button"
            onClick={handleMenuToggle}
            title="Movie options"
          >
            ⋮
          </button>
          {showMenu && (
            <div className="menu-dropdown">
              {showRatings && (
                <button 
                  className="menu-item move-item"
                  onClick={handleMoveToWantToWatch}
                >
                  ⬅️ Move to Want to Watch
                </button>
              )}
              <button 
                className="menu-item delete-item"
                onClick={handleDelete}
              >
                🗑️ Delete Movie
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="movie-info">
        <h3 className="movie-title">{movie.title}</h3>
        <p className="movie-year">{movie.year}</p>
        
        {showRatings && (
          <div className="ratings-section">
            <div className="individual-ratings">
              {/* First User's Rating */}
              <div className="rating-row">
                <span className="rating-label">{isGuest ? 'Alex:' : 'Jackson:'}</span>
                {((!isGuest && currentUser.username === 'jackson') || (isGuest && currentUser.username === 'guest')) && isEditingRating ? (
                  <div className="rating-input-group">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={tempRating}
                      onChange={(e) => setTempRating(e.target.value)}
                      className="rating-input"
                      autoFocus
                    />
                    <button onClick={handleRatingSubmit} className="rating-save">✓</button>
                    <button onClick={handleRatingCancel} className="rating-cancel">✕</button>
                  </div>
                ) : (
                  <div className="rating-display">
                    <span className="rating-value">
                      {getUserRating(isGuest ? 'alex' : 'jackson') ? getUserRating(isGuest ? 'alex' : 'jackson').toFixed(1) : '—'}
                    </span>
                    {((!isGuest && currentUser.username === 'jackson') || (isGuest && currentUser.username === 'guest')) && (
                      <button 
                        onClick={() => setIsEditingRating(true)}
                        className="edit-rating-btn"
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Second User's Rating */}
              <div className="rating-row">
                <span className="rating-label">{isGuest ? 'Sam:' : 'Genna:'}</span>
                {((!isGuest && currentUser.username === 'genna') || (isGuest && currentUser.username === 'guest')) && isEditingRating ? (
                  <div className="rating-input-group">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={tempRating}
                      onChange={(e) => setTempRating(e.target.value)}
                      className="rating-input"
                      autoFocus
                    />
                    <button onClick={handleRatingSubmit} className="rating-save">✓</button>
                    <button onClick={handleRatingCancel} className="rating-cancel">✕</button>
                  </div>
                ) : (
                  <div className="rating-display">
                    <span className="rating-value">
                      {getUserRating(isGuest ? 'sam' : 'genna') ? getUserRating(isGuest ? 'sam' : 'genna').toFixed(1) : '—'}
                    </span>
                    {((!isGuest && currentUser.username === 'genna') || (isGuest && currentUser.username === 'guest')) && (
                      <button 
                        onClick={() => setIsEditingRating(true)}
                        className="edit-rating-btn"
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Third User's Rating (only for demo) */}
              {isGuest && (
                <div className="rating-row">
                  <span className="rating-label">Taylor:</span>
                  <div className="rating-display">
                    <span className="rating-value">
                      {getUserRating('taylor') ? getUserRating('taylor').toFixed(1) : '—'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {getAverageRating() && (
              <div className="average-rating">
                <span className="avg-label">Average:</span>
                <span className="avg-value">{getAverageRating()}/10</span>
              </div>
            )}
          </div>
        )}
        
        <p className="movie-date-added">Added {formatDate(movie.dateAdded)}</p>
      </div>
    </div>
  );
};

export default MovieCard; 